<script setup lang="ts">
import { Modal } from '@/shared/ui/Modal'
import { CancelButton, SaveButton } from '@/shared/ui/ActionButtons'
import { Form, FormItem } from '@/shared/ui/Form'
import { Input } from '@/shared/ui/Input'
import { LoadErrorAlert } from '@/shared/ui/LoadErrorAlert'
import { copyToClipboard } from '@/shared/lib'

/**
 * A value the publisher is meant to take somewhere else — a share link, an embed snippet:
 * a read-only box, one sentence saying what to do with it, and a copy that closes on
 * success and stays open on failure.
 *
 * It is a `Modal` rather than a `FormModal` because nothing is submitted; `Form` is here
 * only to borrow `FormItem`'s label, which is the one place the sentence reads as an
 * instruction rather than as a caption.
 *
 * The default slot sits under the box, inside the same `Form`, and goes away with it: a
 * control that changes the value is not drawn while the value is loading or refused.
 */
const props = withDefaults(
  defineProps<{
    modelValue?: boolean
    title: string
    /** The instruction above the box — a sentence, not a field name. */
    label: string
    /** Empty means there is nothing to copy: the box and the button both stay away. */
    value?: string
    successMessage: string
    textarea?: boolean
    width?: string
    /**
     * Why the value never arrived, for a dialog that has to fetch it. Same contract as
     * `FormModal`'s: the reason replaces the body, and the copy button goes with it —
     * copying a fallback the request never returned is how a wrong snippet reaches a
     * live site.
     */
    loadError?: string
    loading?: boolean
    retrying?: boolean
  }>(),
  { textarea: false, width: '640px', loading: false, retrying: false }
)

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'retry'): void
}>()

async function copy() {
  if (!props.value) return

  const copied = await copyToClipboard(props.value, {
    success: props.successMessage
  })
  if (copied) emit('close')
}
</script>

<template>
  <Modal
    :model-value="modelValue"
    :loading="loading"
    :width="width"
    :title="title"
    @close="emit('close')"
  >
    <LoadErrorAlert
      v-if="loadError"
      :message="loadError"
      :retrying="retrying"
      @retry="emit('retry')"
    />

    <Form v-else-if="value" label-position="top">
      <FormItem :label="label">
        <Input
          :model-value="value"
          :type="textarea ? 'textarea' : 'text'"
          readonly
          :autosize="textarea || undefined"
        />
      </FormItem>
      <slot />
    </Form>

    <template #footer>
      <CancelButton @click="emit('close')">Close</CancelButton>
      <SaveButton v-if="value && !loadError" @click="copy">
        Copy to clipboard
      </SaveButton>
    </template>
  </Modal>
</template>
